import React, { useState } from 'react';
import SideNav from '../components/SideNav';
import { Briefcase, Crown, CheckCircle, Lock, RefreshCw, ArrowUpRight, DollarSign, TrendingUp } from 'lucide-react';

const ExitLab = () => {
    // MOCK DATA: Simulating a user who is 60% of the way to "Freedom"
    const [valuation, setValuation] = useState(1250000); // $1.25M Mock Valuation

    const roles = [
        { id: 1, title: 'MANUFACTURING / PRODUCT', status: 'REPLACED', method: 'OUTSOURCED (3PL)', date: '2025-11-15' },
        { id: 2, title: 'LOGISTICS & FULFILLMENT', status: 'REPLACED', method: 'SYSTEM (AMAZON FBA)', date: '2026-01-10' },
        { id: 3, title: 'SALES & MARKETING', status: 'IN_PROGRESS', method: 'HYBRID (AI + FOUNDER)', date: '---' },
        { id: 4, title: 'FINANCE & OPS', status: 'FOUNDER', method: 'MANUAL', date: '---' },
        { id: 5, title: 'CEO / STRATEGY', status: 'FOUNDER', method: 'MANUAL', date: '---' },
    ];

    return (
        <div style={styles.layout}>
            <SideNav activePage="/exit-lab" />

            <div style={styles.mainContent}>
                {/* HEADER */}
                <div style={styles.header}>
                    <div>
                        <h1 style={styles.pageTitle}>EXIT LAB <span style={styles.betaTag}>ALPHA PREVIEW</span></h1>
                        <p style={styles.subTitle}>STEP 9-10: SYSTEMATIZATION & ACQUISITION READINESS</p>
                    </div>
                    <div style={styles.statusBadge}>
                        <TrendingUp size={16} /> VALUATION TRACKING ACTIVE
                    </div>
                </div>

                <div style={styles.gridContainer}>

                    {/* LEFT: THE REPLACEMENT LADDER */}
                    <div style={styles.leftPanel}>
                        <div style={styles.panelHeader}>
                            <RefreshCw size={18} color="#D4AF37" />
                            <span>ROLE REPLACEMENT MATRIX</span>
                        </div>
                        <p style={styles.panelDesc}>Identify bottleneck roles and deploy systems to replace the Founder.</p>

                        <div style={styles.roleList}>
                            {roles.map((role, index) => (
                                <div key={role.id} style={styles.roleCard}>
                                    <div style={styles.roleIcon}>
                                        {index === 4 ? <Crown size={20} color="#D4AF37" /> : <Briefcase size={20} color="#666" />}
                                    </div>
                                    <div style={styles.roleInfo}>
                                        <div style={styles.roleTitle}>{role.title}</div>
                                        <div style={styles.roleMethod}>METHOD: {role.method}</div>
                                    </div>
                                    <div style={styles.roleStatus}>
                                        {role.status === 'REPLACED' ? (
                                            <span style={styles.statusDone}><CheckCircle size={14} /> SYSTEM</span>
                                        ) : role.status === 'IN_PROGRESS' ? (
                                            <span style={styles.statusProgress}><RefreshCw size={14} /> TRANSITION</span>
                                        ) : (
                                            <span style={styles.statusLocked}><Lock size={14} /> FOUNDER</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT: FREEDOM METRICS */}
                    <div style={styles.rightPanel}>

                        {/* FREEDOM SCORE CARD */}
                        <div style={styles.metricCard}>
                            <div style={styles.metricHeader}>
                                <ArrowUpRight size={24} color="#D4AF37" />
                                <h3>FREEDOM SCORE</h3>
                            </div>
                            <div style={styles.freedomMeter}>
                                <div style={styles.meterFill}></div>
                            </div>
                            <div style={styles.meterLabels}>
                                <span>0% (SLAVE)</span>
                                <span style={{ color: '#D4AF37', fontWeight: 'bold' }}>40% AUTONOMOUS</span>
                                <span>100% (OWNER)</span>
                            </div>
                        </div>

                        {/* VALUATION CARD */}
                        <div style={styles.metricCard}>
                            <div style={styles.metricHeader}>
                                <DollarSign size={24} color="#4caf50" />
                                <h3>EST. EXIT VALUATION</h3>
                            </div>
                            <div style={styles.bigNumber}>${valuation.toLocaleString()}</div>
                            <div style={styles.subText}>*Based on current automation & EBITA multiplier (3.5x)</div>
                        </div>

                        {/* AI RECOMMENDATION */}
                        <div style={styles.aiBox}>
                            <div style={styles.aiTitle}>MILLION STRATEGY:</div>
                            <p>
                                "You have successfully removed yourself from <strong>Labor & Logistics</strong>. Your next bottleneck is <strong>Sales</strong>.
                                Recommendation: Utilize the Million to discuss options."
                            </p>
                            <button style={styles.actionBtn}>INITIATE SALES AUTOMATION</button>
                        </div>

                    </div>

                </div>
            </div>
        </div>
    );
};

const styles = {
    layout: { display: 'flex', minHeight: '100vh', background: '#000', color: '#fff', fontFamily: 'Inter, sans-serif' },
    mainContent: { flex: 1, padding: '30px', display: 'flex', flexDirection: 'column' },

    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', borderBottom: '1px solid #222', paddingBottom: '20px' },
    pageTitle: { fontFamily: 'Orbitron', fontSize: '2rem', color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '15px' },
    betaTag: { fontSize: '0.8rem', background: '#222', color: '#D4AF37', padding: '5px 10px', borderRadius: '4px', border: '1px solid #333' },
    subTitle: { color: '#666', margin: '5px 0 0 0', fontSize: '0.9rem', letterSpacing: '1px' },
    statusBadge: { display: 'flex', alignItems: 'center', gap: '8px', color: '#4caf50', fontSize: '0.9rem', fontFamily: 'Orbitron' },

    gridContainer: { display: 'flex', gap: '30px', flex: 1 },

    // LEFT PANEL
    leftPanel: { flex: 3, background: '#0a0a0a', border: '1px solid #333', borderRadius: '8px', padding: '20px' },
    panelHeader: { display: 'flex', alignItems: 'center', gap: '10px', fontFamily: 'Orbitron', color: '#D4AF37', fontSize: '1.2rem', marginBottom: '5px' },
    panelDesc: { color: '#666', fontSize: '0.9rem', marginBottom: '20px' },

    roleList: { display: 'flex', flexDirection: 'column', gap: '15px' },
    roleCard: { display: 'flex', alignItems: 'center', background: '#111', padding: '20px', borderRadius: '4px', border: '1px solid #222' },
    roleIcon: { width: '40px', display: 'flex', justifyContent: 'center' },
    roleInfo: { flex: 1, paddingLeft: '15px' },
    roleTitle: { fontFamily: 'Orbitron', fontSize: '1rem', color: '#fff' },
    roleMethod: { fontSize: '0.8rem', color: '#888', marginTop: '5px' },

    roleStatus: { width: '120px', textAlign: 'right' },
    statusDone: { color: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px', fontSize: '0.8rem', fontWeight: 'bold' },
    statusProgress: { color: 'orange', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px', fontSize: '0.8rem', fontWeight: 'bold' },
    statusLocked: { color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px', fontSize: '0.8rem', fontWeight: 'bold' },

    // RIGHT PANEL
    rightPanel: { flex: 2, display: 'flex', flexDirection: 'column', gap: '20px' },

    metricCard: { background: '#111', border: '1px solid #333', padding: '25px', borderRadius: '8px' },
    metricHeader: { display: 'flex', alignItems: 'center', gap: '10px', fontFamily: 'Orbitron', color: '#fff', fontSize: '0.9rem', marginBottom: '20px' },

    freedomMeter: { height: '10px', background: '#222', borderRadius: '5px', overflow: 'hidden', marginBottom: '10px' },
    meterFill: { width: '40%', height: '100%', background: 'linear-gradient(90deg, #D4AF37, #f1c40f)' },
    meterLabels: { display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#666', fontFamily: 'Orbitron' },

    bigNumber: { fontSize: '3rem', fontFamily: 'Orbitron', color: '#4caf50' },
    subText: { fontSize: '0.8rem', color: '#666', marginTop: '5px' },

    aiBox: { background: 'rgba(212, 175, 55, 0.1)', border: '1px solid #D4AF37', padding: '20px', borderRadius: '8px', marginTop: 'auto' },
    aiTitle: { color: '#D4AF37', fontFamily: 'Orbitron', marginBottom: '10px', fontWeight: 'bold' },
    actionBtn: { width: '100%', background: '#D4AF37', color: '#000', border: 'none', padding: '12px', marginTop: '15px', fontFamily: 'Orbitron', fontWeight: 'bold', cursor: 'pointer' }
};

export default ExitLab;